import * as React from 'react';
import styles from './index.less';
import * as AHooks from 'ahooks';
import FComponentsLib from '@freelog/components-lib';
import { FI18n, FUtil } from '@freelog/tools-lib';
// import FSingleLineInput from '@/components/FSingleLineInput';
import { IAttrsAndConfigsItem } from '../..';
import { ConfigProvider, DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);
import FSelect2 from '@/components/FSelect2';
import FIcons from '@/components/FIcons';
import enUS from 'antd/locale/en_US';
import zhCN from 'antd/locale/zh_CN';
import FInput_PinyinSafeTextCounter from '@/components/FInput_PinyinSafeTextCounter';
import FInput_PinyinSafeTextCounter_MultiLine from '@/components/FInput_PinyinSafeTextCounter_MultiLine';

interface EditValuePopoverProps {
  name: string;
  value: string;
  configs: Exclude<IAttrsAndConfigsItem['configs']['value_Editable'], false>;
  onCancel?: () => void;
  onOk?: (value: string) => void;
}

function EditValuePopover({ name, value, configs, onOk, onCancel }: EditValuePopoverProps) {
  const ref_editValuePopover = React.useRef<HTMLDivElement>(null);

  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>(value);
  const [$valueError, set$valueError, get$valueError] = FUtil.Hook.useGetState<string>('');
  const [$dirty, set$dirty, get$dirty] = FUtil.Hook.useGetState<boolean>(false);

  AHooks.useClickAway(
    () => {
      onCancel?.();
    },
    ref_editValuePopover,
    [
      'click',
      // 'contextmenu'
    ],
  );

  return (
    <div ref={ref_editValuePopover} className={styles.editValuePopover}>
      <div className={styles.title}>{name}</div>
      <div style={{ height: 5 }} />

      {configs.text && (
        <Text
          value={$value}
          config={configs.text}
          onChange={(value, valueError) => {
            set$value(value);
            set$valueError(valueError);
            set$dirty(true);
          }}
          hasError={$valueError !== ''}
        />
      )}

      {configs.textArea && (
        <TextArea
          value={$value}
          config={configs.textArea}
          onChange={(value, valueError) => {
            set$value(value);
            set$valueError(valueError);
            set$dirty(true);
          }}
          hasError={$valueError !== ''}
        />
      )}

      {configs.integer && (
        <Integer
          value={$value}
          config={configs.integer}
          onChange={(value, valueError) => {
            set$value(value);
            set$valueError(valueError);
            set$dirty(true);
          }}
          hasError={$valueError !== ''}
        />
      )}

      {configs.decimal && (
        <Decimal
          value={$value}
          config={configs.decimal}
          onChange={(value, valueError) => {
            set$value(value);
            set$valueError(valueError);
            set$dirty(true);
          }}
          hasError={$valueError !== ''}
        />
      )}

      {configs.date && (
        <Date
          value={$value}
          config={configs.date}
          onChange={(value, valueError) => {
            set$value(value);
            set$valueError(valueError);
            set$dirty(true);
          }}
          refContainer={ref_editValuePopover}
          hasError={$valueError !== ''}
        />
      )}

      {configs.dataTime && (
        <DateTime
          value={$value}
          config={configs.dataTime}
          onChange={(value, valueError) => {
            set$value(value);
            set$valueError(valueError);
            set$dirty(true);
          }}
          refContainer={ref_editValuePopover}
          hasError={$valueError !== ''}
        />
      )}
      {configs.configEnum && (
        <ConfigEnum
          value={$value}
          config={configs.configEnum}
          onChange={(value, valueError) => {
            set$value(value);
            set$valueError(valueError);
            set$dirty(true);
          }}
          refContainer={ref_editValuePopover}
          hasError={$valueError !== ''}
        />
      )}
      {configs.configText && (
        <ConfigText
          value={$value}
          config={configs.configText}
          onChange={(value, valueError) => {
            set$value(value);
            set$valueError(valueError);
            set$dirty(true);
          }}
          hasError={$valueError !== ''}
        />
      )}

      {$valueError !== '' && (
        <>
          <div style={{ height: 5 }} />
          <div
            style={{
              color: '#EE4040',
              fontSize: 12,
            }}
          >
            {$valueError}
          </div>
        </>
      )}

      <div style={{ height: 20 }} />
      <div className={styles.footer}>
        <FComponentsLib.FTextBtn
          type={'default'}
          onClick={() => {
            onCancel?.();
          }}
          style={{ fontSize: 12 }}
        >
          {FI18n.i18nNext.tAuto('btn_cancel')}
        </FComponentsLib.FTextBtn>
        <FComponentsLib.FRectBtn
          disabled={!$dirty || $valueError !== ''}
          type={'primary'}
          onClick={() => {
            onOk?.(get$value());
          }}
          style={{ fontSize: 12 }}
        >
          {FI18n.i18nNext.tAuto('btn_save')}
        </FComponentsLib.FRectBtn>
      </div>
    </div>
  );
}

export default EditValuePopover;

interface TextProps {
  value: string;
  config: NonNullable<Exclude<IAttrsAndConfigsItem['configs']['value_Editable'], false>['text']>;
  onChange(value: string, valueError: string): void;
  hasError?: boolean;
}

function Text({ value, config, onChange, hasError = false }: TextProps) {
  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>(value);

  return (
    // <FSingleLineInput
    <FInput_PinyinSafeTextCounter
      hasError={hasError}
      style={{
        width: '100%',
        border: '1px solid #d9d9d9',
      }}
      lengthLimit={-1}
      // lengthLimit={config.maxLength}
      value={$value}
      onChangeValue={(valueInput) => {
        let errorText: string = '';
        if (valueInput === '') {
          if (config.nullable) {
            errorText = '';
          } else {
            errorText = '请输入';
          }
        } else if (config.minLength && valueInput.length < config.minLength) {
          errorText = `不少于${config.minLength}个字符`;
        } else if (config.maxLength && valueInput.length > config.maxLength) {
          errorText = `不超过${config.maxLength}个字符`;
        }
        set$value(valueInput);
        onChange(valueInput, errorText);
      }}
      placeholder={FI18n.i18nNext.t('resourceinfo_add_input_value_hint')}
      // hasError={}
    />
  );
}

interface TextAreaProps {
  value: string;
  config: NonNullable<
    Exclude<IAttrsAndConfigsItem['configs']['value_Editable'], false>['textArea']
  >;
  onChange(value: string, valueError: string): void;
  hasError?: boolean;
}

function TextArea({ value, config, onChange, hasError = false }: TextAreaProps) {
  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>(value);

  return (
    // <FComponentsLib.FInput.FMultiLine
    <FInput_PinyinSafeTextCounter_MultiLine
      hasError={hasError}
      style={{ width: '100%' }}
      lengthLimit={-1}
      // lengthLimit={config.maxLength}
      value={$value}
      onChangeValue={(valueInput) => {
        let errorText: string = '';
        if (valueInput === '') {
          if (config.nullable) {
            errorText = '';
          } else {
            errorText = '请输入';
          }
        } else if (config.minLength && valueInput.length < config.minLength) {
          errorText = `不少于${config.minLength}个字符`;
        } else if (config.maxLength && valueInput.length > config.maxLength) {
          errorText = `不超过${config.maxLength}个字符`;
        }
        set$value(valueInput);
        onChange(valueInput, errorText);
      }}
    />
  );
}

interface IntegerProps {
  value: string;
  config: NonNullable<Exclude<IAttrsAndConfigsItem['configs']['value_Editable'], false>['integer']>;
  onChange(value: string, valueError: string): void;
  hasError?: boolean;
}

function Integer({ value, config, onChange, hasError = false }: IntegerProps) {
  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>(value);

  function onVerify_integer() {
    const valueInput = get$value();
    let errorText: string = '';
    let num = Number.parseInt(valueInput);
    if (valueInput === '') {
      if (config.nullable) {
        errorText = '';
      } else {
        errorText = '请输入';
      }
    } else if (Number.isNaN(num)) {
      errorText = '请输入正确的整数';
    } else if (!Number.isSafeInteger(num)) {
      if (num < 0) {
        errorText = '不小于-99999999999';
      } else {
        errorText = '不小于99999999999';
      }
    } else if (num < -99999999999) {
      errorText = '不小于-99999999999';
    } else if (num > 99999999999) {
      errorText = '不大于99999999999';
    } else if (config.min !== undefined && num < config.min) {
      errorText = `不小于${config.min}`;
    } else if (config.max && num > config.max) {
      errorText = `不大于${config.max}`;
    }
    if (!Number.isNaN(num)) {
      set$value(String(num));
    }

    onChange(valueInput, errorText);
  }

  return (
    // <FSingleLineInput
    <FInput_PinyinSafeTextCounter
      hasError={hasError}
      style={{
        width: '100%',
        border: '1px solid #d9d9d9',
      }}
      lengthLimit={-1}
      value={$value}
      onChangeValue={(valueInput) => {
        set$value(valueInput);
        onVerify_integer();
      }}
      placeholder={'请输入整数'}
    />
  );
}

interface DecimalProps {
  value: string;
  config: NonNullable<Exclude<IAttrsAndConfigsItem['configs']['value_Editable'], false>['decimal']>;
  onChange(value: string, valueError: string): void;
  hasError?: boolean;
}

function Decimal({ value, config, onChange, hasError = false }: DecimalProps) {
  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>(value);

  function onVerify_decimal() {
    const valueInput = get$value();
    let errorText: string = '';
    let num = Number.parseFloat(valueInput);
    if (valueInput === '') {
      if (config.nullable) {
        errorText = '';
      } else {
        errorText = '请输入';
      }
    } else if (Number.isNaN(num)) {
      errorText = '请输入正确的小数';
    } else {
      num = Math.floor(num * 10 ** config.precision || 0) / 10 ** config.precision;

      if (num < config.minDecimal) {
        errorText = `不小于${config.minDecimal}`;
      } else if (num > config.maxDecimal) {
        errorText = `不大于${config.maxDecimal}`;
      }
    }
    set$value(String(num));
    onChange(valueInput, errorText);
  }

  return (
    // <FSingleLineInput
    <FInput_PinyinSafeTextCounter
      hasError={hasError}
      style={{
        width: '100%',
        border: '1px solid #d9d9d9',
      }}
      lengthLimit={-1}
      value={$value}
      onChangeValue={(valueInput) => {
        set$value(valueInput);
        onVerify_decimal();
      }}
      placeholder={'请输入小数'}
    />
  );
}

interface DateProps {
  value: string;
  config: NonNullable<Exclude<IAttrsAndConfigsItem['configs']['value_Editable'], false>['date']>;
  onChange(value: string, valueError: string): void;
  refContainer: React.RefObject<HTMLDivElement>;
  hasError?: boolean;
}

function Date({ value, config, onChange, refContainer, hasError = false }: DateProps) {
  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>('');

  AHooks.useMount(() => {
    const dateRegex: RegExp = new RegExp(
      /(([0-9]{3}[1-9]|[0-9]{2}[1-9][0-9]{1}|[0-9]{1}[1-9][0-9]{2}|[1-9][0-9]{3})-(((0[13578]|1[02])-(0[1-9]|[12][0-9]|3[01]))|((0[469]|11)-(0[1-9]|[12][0-9]|30))|(02-(0[1-9]|[1][0-9]|2[0-8]))))|((([0-9]{2})(0[48]|[2468][048]|[13579][26])|((0[48]|[2468][048]|[3579][26])00))-02-29)$/,
    );
    if (dateRegex.test(value)) {
      set$value(value);
    }
  });

  function onVerify_date() {
    const startDate = config.startDate.split(' ')[0];
    const limitDate = config.limitDate.split(' ')[0];
    const valueInput = get$value();
    let errorText: string = '';
    const formatString = 'YYYY-MM-DD';
    // const valueFormat = get$valueFormat();
    if (valueInput === '') {
      if (config.nullable) {
        errorText = '';
      } else {
        errorText = '请输入';
      }
    } else if (
      dayjs(valueInput, formatString).isBefore(
        dayjs(startDate + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss'),
      )
    ) {
      errorText = `时间不能早于 ${startDate}`;
    } else if (
      dayjs(valueInput, formatString).isAfter(
        dayjs(limitDate + ' 23:59:59', 'YYYY-MM-DD HH:mm:ss'),
      )
    ) {
      errorText = `时间不能晚于 ${limitDate}`;
    } else {
      errorText = '';
    }

    onChange(valueInput, errorText);
  }

  return (
    <ConfigProvider locale={FI18n.i18nNext.getCurrentLanguage() === 'zh_CN' ? zhCN : enUS}>
      <DatePicker
        value={$value ? dayjs($value, 'YYYY-MM-DD') : null}
        style={{ width: '100%', height: 38 }}
        // Figma：选择日期 suffix icon
        suffixIcon={<FIcons.FDate style={{ color: '#222', fontSize: 14 }} />}
        onChange={(value, dateString: string | string[]) => {
          set$value(dateString as string);
          onVerify_date();
        }}
        disabledDate={(currentDate) => {
          let result: boolean = false;
          // const valueFormat = config;
          if (config.startDate) {
            const startDate = config.startDate.split(' ')[0];
            result = currentDate.isBefore(dayjs(startDate + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss'));
          }

          if (!result && config.limitDate) {
            const limitDate = config.limitDate.split(' ')[0];
            result = currentDate.isAfter(dayjs(limitDate + ' 23:59:59', 'YYYY-MM-DD HH:mm:ss'));
          }
          return result;
        }}
        showNow={false}
        showToday={false}
        inputReadOnly={true}
        getPopupContainer={() => {
          return refContainer.current!;
        }}
      />
    </ConfigProvider>
  );
}

interface DateTimeProps {
  value: string;
  config: NonNullable<
    Exclude<IAttrsAndConfigsItem['configs']['value_Editable'], false>['dataTime']
  >;

  onChange(value: string, valueError: string): void;
  refContainer: React.RefObject<HTMLDivElement>;
  hasError?: boolean;
}

function DateTime({ value, config, onChange, refContainer, hasError = false }: DateTimeProps) {
  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>('');

  AHooks.useMount(() => {
    const timeRegex: RegExp = new RegExp(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/);
    if (timeRegex.test(value)) {
      set$value(value);
    }
  });

  function onVerify_dataTime() {
    const valueInput = get$value();
    let errorText: string = '';
    const formatString = 'YYYY-MM-DD HH:mm:ss';
    if (valueInput === '') {
      if (config.nullable) {
        errorText = '';
      } else {
        errorText = '请输入';
      }
    } else if (
      dayjs(valueInput, formatString).isBefore(dayjs(config.startDateTime, formatString))
    ) {
      errorText = `时间不能早于 ${config.startDateTime}`;
    } else if (
      dayjs(valueInput, formatString).isAfter(dayjs(config.limitDateTime, formatString))
    ) {
      errorText = `时间不能晚于 ${config.limitDateTime}`;
    } else {
      errorText = '';
    }
    onChange(valueInput, errorText);
  }

  return (
    <ConfigProvider locale={FI18n.i18nNext.getCurrentLanguage() === 'zh_CN' ? zhCN : enUS}>
      <DatePicker
        value={$value ? dayjs($value, 'YYYY-MM-DD HH:mm:ss') : null}
        showTime={true}
        style={{ width: '100%', height: 38 }}
        // Figma：时间/时长选择 suffix icon
        suffixIcon={<FIcons.FTime style={{ color: '#222', fontSize: 14 }} />}
        onChange={(value, dateString: string | string[]) => {
          set$value(dateString as string);
          onVerify_dataTime();
        }}
        disabledDate={(currentDate) => {
          let result: boolean = false;
          // const valueFormat = get$valueFormat();
          if (config.startDateTime) {
            result = currentDate.isBefore(dayjs(config.startDateTime, 'YYYY-MM-DD HH:mm:ss'));
          }

          if (!result && config.limitDateTime) {
            result = currentDate.isAfter(dayjs(config.limitDateTime, 'YYYY-MM-DD HH:mm:ss'));
          }
          return result;
        }}
        // showToday=false 时 rc-picker 会把「现在」一并关掉，需显式打开
        showNow={true}
        showToday={false}
        inputReadOnly={true}
        // disabledTime={(data) => {
        //   // console.log(data, partial, 'disabledTime dsf disabledTime dsfwefwes disabledTime 0932ujrilsfdujlfkjl');
        //   return {
        //     disabledHours() {
        //       return [15, 16, 17];
        //     },
        //     disabledMinutes(selectedHour: number) {
        //
        //       return Array(60).fill(null).map((m, i) => {
        //         return i;
        //       });
        //     },
        //     disabledSeconds(selectedHour: number, selectedMinute: number) {
        //       return Array(60).fill(null).map((m, i) => {
        //         return i;
        //       });
        //     },
        //   };
        // }}
        getPopupContainer={() => {
          return refContainer.current!;
        }}
      />
    </ConfigProvider>
  );
}

interface ConfigEnumProps {
  value: string;
  config: NonNullable<
    Exclude<IAttrsAndConfigsItem['configs']['value_Editable'], false>['configEnum']
  >;
  onChange(value: string, valueError: string): void;
  refContainer: React.RefObject<HTMLDivElement>;
  hasError?: boolean;
}

function ConfigEnum({ value, config, onChange, refContainer, hasError = false }: ConfigEnumProps) {
  // const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>(value);
  // console.log('******9999999999999999999************', value);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <FSelect2
        // hasError={hasError}
        value={value}
        options={config.options.map((item) => {
          return {
            label: item,
            value: item,
          };
        })}
        style={{ width: 'calc(100% - 20px)', height: 38 }}
        onChange={(value) => {
          // set$value(value);
          onChange(value, '');
        }}
        getPopupContainer={() => {
          return refContainer.current!;
        }}
      />
      <FComponentsLib.FTextBtn
        onClick={() => {
          onChange(config.defaultValue, '');
        }}
      >
        <FIcons.FRedo style={{ fontSize: 12 }} />
      </FComponentsLib.FTextBtn>
    </div>
  );
}

interface ConfigTextProps {
  value: string;
  config: NonNullable<
    Exclude<IAttrsAndConfigsItem['configs']['value_Editable'], false>['configText']
  >;
  onChange(value: string, valueError: string): void;
  hasError?: boolean;
}

function ConfigText({ value, config, onChange, hasError = false }: ConfigTextProps) {
  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>(value);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {/* <FSingleLineInput */}
      <FInput_PinyinSafeTextCounter
        hasError={hasError}
        style={{
          width: 'calc(100% - 20px)',
          border: '1px solid #d9d9d9',
        }}
        lengthLimit={-1}
        // lengthLimit={config.maxLength}
        value={$value}
        onChangeValue={(valueInput) => {
          let errorText: string = '';
          if (valueInput === '') {
            if (config.nullable) {
              errorText = '';
            } else {
              errorText = '请输入';
            }
          } else if (config.minLength && valueInput.length < config.minLength) {
            errorText = `不少于${config.minLength}个字符`;
          } else if (config.maxLength && valueInput.length > config.maxLength) {
            errorText = `不超过${config.maxLength}个字符`;
          }
          set$value(valueInput);
          onChange(valueInput, errorText);
        }}
        placeholder={'请输入'}
      />

      <FComponentsLib.FTextBtn
        onClick={() => {
          set$value(config.defaultValue);
          onChange(config.defaultValue, '');
        }}
      >
        <FIcons.FRedo style={{ fontSize: 12 }} />
      </FComponentsLib.FTextBtn>
    </div>
  );
}
